/**
 * Deterministic SKU code → color. Same code always produces the same color,
 * across sessions and reloads, so operators can build muscle memory for
 * "the blue tag is the body lotion." Hash-to-hue with fixed saturation and
 * lightness keeps every color readable on the cream canvas (fs-canvas) and
 * legible as text-on-fill in the SKU label tag.
 */

const SATURATION = 65;
const LIGHTNESS_STROKE = 42;
const LIGHTNESS_FILL = 46;
const ON_LIGHTNESS = 96;

function hashCode(code: string): number {
  let hash = 2166136261; // FNV-1a 32-bit offset basis
  for (let i = 0; i < code.length; i++) {
    hash ^= code.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export type SkuColor = {
  stroke: string;
  fill: string;
  onColor: string;
};

export function skuColor(code: string): SkuColor {
  const hue = hashCode(code) % 360;
  return {
    stroke: `hsl(${hue}, ${SATURATION}%, ${LIGHTNESS_STROKE}%)`,
    fill: `hsl(${hue}, ${SATURATION}%, ${LIGHTNESS_FILL}%)`,
    onColor: `hsl(${hue}, ${SATURATION}%, ${ON_LIGHTNESS}%)`,
  };
}

/**
 * Return the same color with the given alpha. Accepts `hsl(h, s%, l%)` and
 * `#rrggbb` (the two formats we actually emit). Falls through to the input
 * for other formats — Konva's canvas will paint it as-is.
 */
export function withAlpha(color: string, alpha: number): string {
  const hsl = /^hsl\((\s*-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?%)\s*,\s*(-?\d+(?:\.\d+)?%)\s*\)$/.exec(
    color,
  );
  if (hsl) return `hsla(${hsl[1]}, ${hsl[2]}, ${hsl[3]}, ${alpha})`;
  const hex = /^#([0-9a-f]{6})$/i.exec(color);
  if (hex) {
    const n = parseInt(hex[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}
